/**
 * TanStack Query hooks for the assistant routes.
 *
 * These follow the same pattern as `src/lib/api/hooks.ts` — one hook per
 * endpoint, a stable `queryKey`, and explicit invalidation from the mutations —
 * and move there wholesale once the endpoints exist in the generated SDK.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { IncidentCreateRequest, TicketRef } from "@/features/tickets";
import { createTicket, newIdempotencyKey } from "@/features/tickets/api/operations";
import { type OnBehalfPerson, raisedForName } from "@/features/tickets/lib/on-behalf";

import {
  cancelAssistantMessage,
  createConversation,
  deleteConversation,
  getConversation,
  listConversations,
  postAssistantMessage,
  prepareServiceRequest,
  submitServiceRequest,
} from "./api";
import type {
  AssistantConversationCreateRequest,
  AssistantMessageCreateRequest,
  ServiceRequestPrepareRequest,
  ServiceRequestSubmission,
  ServiceRequestSubmitRequest,
} from "./contract";

/**
 * Every query key this feature owns, in one place.
 *
 * Building keys through helpers rather than inline arrays is what makes
 * `invalidateQueries({ queryKey: assistantKeys.conversations() })` reliably
 * catch every variant of a list query, whatever filters it was called with.
 */
export const assistantKeys = {
  all: ["assistant"] as const,
  conversations: () => [...assistantKeys.all, "conversations"] as const,
  conversationList: (params: { q?: string; limit?: number; offset?: number }) =>
    [...assistantKeys.conversations(), params] as const,
  conversation: (conversationId: string, translateTo?: string) =>
    [...assistantKeys.all, "conversation", conversationId, translateTo ?? null] as const,
  /**
   * The request one inline form has already filed.
   *
   * Not a query anyone runs — it is a cache slot, written by
   * {@link useSubmitServiceRequest} and read back by the card. The card
   * unmounts and remounts when a finished turn hands over from the live stream
   * to the persisted transcript, and component state does not survive that.
   * This does, so a filed request stays filed and the button stays disabled.
   */
  serviceRequest: (formKey: string) => [...assistantKeys.all, "service-request", formKey] as const,
  /** A chat form the person cancelled (14 Oct): stays cancelled across a remount. */
  formCancelled: (formKey: string) => [...assistantKeys.all, "form-cancelled", formKey] as const,
  /**
   * The incident one inline incident form has already raised.
   *
   * The same cache slot as {@link assistantKeys.serviceRequest} and for the
   * same reason: the card remounts when a finished turn hands over from the
   * live stream to the persisted transcript, and a raised incident must stay
   * raised across that.
   */
  incidentTicket: (formKey: string) => [...assistantKeys.all, "incident-ticket", formKey] as const,
  /** Who an inline incident was raised for, when it was not the signed-in person. */
  /** "Yes, add me to it" (8 Oct): the open ticket this card joined instead of filing. */
  incidentJoined: (formKey: string) => [...assistantKeys.all, "incident-joined", formKey] as const,
  incidentRaisedFor: (formKey: string) =>
    [...assistantKeys.all, "incident-raised-for", formKey] as const,
} as const;

// ─── Conversations ───────────────────────────────────────────────────────

export interface UseConversationsOptions {
  limit?: number;
  offset?: number;
  q?: string;
  enabled?: boolean;
}

/** The signed-in user's conversation history, newest first (server-ordered). */
export function useConversations(options: UseConversationsOptions = {}) {
  const { enabled = true, ...params } = options;
  return useQuery({
    queryKey: assistantKeys.conversationList(params),
    queryFn: () => listConversations(params),
    enabled,
  });
}

/**
 * One conversation with its messages.
 *
 * `translateTo` is opt-in per the contract's §2 rule: absent, the transcript
 * comes back in whatever language each message was written in.
 */
export function useConversation(
  conversationId: string | null,
  options: { limit?: number; translateTo?: string } = {},
) {
  const { limit = 50, translateTo } = options;
  return useQuery({
    queryKey: assistantKeys.conversation(conversationId ?? "", translateTo),
    queryFn: () =>
      getConversation(conversationId as string, {
        limit,
        ...(translateTo === undefined ? {} : { translate_to: translateTo }),
      }),
    enabled: conversationId !== null,
  });
}

export function useCreateConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AssistantConversationCreateRequest) => createConversation(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: assistantKeys.conversations() });
    },
  });
}

export function useDeleteConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) => deleteConversation(conversationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: assistantKeys.conversations() });
    },
  });
}

export interface PostMessageVariables {
  conversationId: string;
  body: AssistantMessageCreateRequest;
}

/**
 * Enqueue the user's turn.
 *
 * Resolves with the reserved assistant message and the `stream_path` to open —
 * it does not wait for the model. The transcript is refreshed by the stream's
 * `done` event, not by this mutation, so the list is invalidated only for its
 * ordering and preview text.
 */
export function usePostAssistantMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId, body }: PostMessageVariables) =>
      postAssistantMessage(conversationId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: assistantKeys.conversations() });
    },
  });
}

export interface CancelMessageVariables {
  conversationId: string;
  messageId: string;
}

export function useCancelAssistantMessage() {
  return useMutation({
    mutationFn: ({ conversationId, messageId }: CancelMessageVariables) =>
      cancelAssistantMessage(conversationId, messageId),
  });
}

// ─── Inline request forms ────────────────────────────────────────────────

/** Validate a filled-in offering form. Never retried — it is cheap to press again. */
export function usePrepareServiceRequest() {
  return useMutation({
    mutationFn: (body: ServiceRequestPrepareRequest) => prepareServiceRequest(body),
    retry: false,
  });
}

/**
 * File a prepared request and remember that it was filed.
 *
 * `retry: false` because this is the call that creates a ticket in Ivanti, and an automatic replay is how one
 * request becomes two. The result is written into the query cache under
 * `formKey` so the card can refuse a second submission even after a remount.
 */
export function useSubmitServiceRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      formKey: _formKey,
      body,
      idempotencyKey,
    }: {
      formKey: string;
      body: ServiceRequestSubmitRequest;
      /**
       * The caller's per-attempt key: the same across a retry of the same
       * answers (so a timeout pressed through replays the first request
       * instead of filing a twin), new once the answers change.
       */
      idempotencyKey?: string;
    }) => submitServiceRequest(body, idempotencyKey),
    retry: false,
    onSuccess: (submission, { formKey }) => {
      queryClient.setQueryData<ServiceRequestSubmission>(
        assistantKeys.serviceRequest(formKey),
        submission,
      );
      // The new ticket belongs in "my tickets" and in the assistant's own
      // conversation list preview, both of which are now one row out of date.
      void queryClient.invalidateQueries({ queryKey: ["tickets"] });
    },
  });
}

/**
 * Raise an incident from the transcript, and remember that it was raised.
 *
 * The same shape as {@link useSubmitServiceRequest}: `retry: false`, because
 * this is the call that creates a ticket in Ivanti and an automatic replay is
 * how one incident becomes two, and the result written into the query cache
 * under `formKey` so the card refuses a second submission even after a remount.
 *
 * `idempotencyKey` is the caller's to hold: it stays the same across a retry of
 * the *same* answers, so a timeout the user presses through cannot duplicate
 * the ticket, and changes when they edit and try again.
 */
export function useRaiseIncident() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      formKey: _formKey,
      body,
      idempotencyKey,
    }: {
      formKey: string;
      body: IncidentCreateRequest;
      idempotencyKey?: string;
      /** The colleague it is raised for; null for the signed-in person. */
      forPerson?: OnBehalfPerson | null;
    }) => createTicket(body, idempotencyKey ?? newIdempotencyKey()),
    retry: false,
    onSuccess: (detail, { formKey, forPerson }) => {
      if (forPerson != null) {
        // Kept with the ticket ref so a remounted card still says whose it is.
        queryClient.setQueryData<string>(
          assistantKeys.incidentRaisedFor(formKey),
          raisedForName(detail.requester, forPerson) ?? forPerson.displayName,
        );
      }
      const ref: TicketRef = {
        ticket_id: detail.ticket_id,
        ticket_number: detail.ticket_number,
        kind: detail.kind,
        status_code: detail.status.code,
        subject: detail.subject,
        url_path: detail.url_path.length > 0 ? detail.url_path : `/tickets/${detail.ticket_id}`,
      };
      queryClient.setQueryData<TicketRef>(assistantKeys.incidentTicket(formKey), ref);
      // The new ticket belongs in "my tickets" and in the assistant's own
      // conversation list preview, both of which are now one row out of date.
      void queryClient.invalidateQueries({ queryKey: ["tickets"] });
    },
  });
}
